/**
 * Created by Bowen on 5/24/2020.
 * Description: Add contact body component
 * ------ maintenance history ------
 * Update by Marcus Zhao  10/21/2021 - Added export feature.
 * Update by Marcus Zhao  06/12/2022 - Support to set one way or two way synch status.
 * Update by Marcus Zhao  07/22/2022 - Use CONTACT_VALUE_CHANGE as action to trigger contact change.
 * Update by Daniel Wang  09/05/2022 - Show connect/sync button in contact detail.
 */
import { SlideSheetService } from './../../../../services/slide-sheet.service';
import { ContactService } from './../../../../tamalelibs/services/contact.service';
import { catchError, filter, take } from 'rxjs/operators';
import { Component, OnInit, Input, ChangeDetectorRef } from '@angular/core';
import { Contact } from '../../../../tamalelibs/models/contact.model';
import { SlideSheetActionTypes, SlideSheetEventTypes } from '../../../slide-sheet/slide-sheet.model';
import { AppConfig } from '../../../../tamalelibs/models/app-config.model';
import { of as observableOf, Subscription } from 'rxjs';
import { ContactInformationItemSubject, TriggerSource } from '../contact-information-item/contact-information-item.model';
import { ToastService } from '../../../../widgets/toast/toast.service';
import { StoreQuerierService } from '../../../../services/store-querier.service';
import { getSystemConfigState } from '../../../../redux/reducers/system-config-view.reducer';
import { businessConstants } from '../../../../constants/business.constants';
import { NotificationStyles } from '../../../../widgets/notification/notification.model';
import { StringLiteralsPipe } from '../../../../pipes/translate.pipe';

@Component({
    selector: 'tam-contact-profile',
    templateUrl: './contact-profile.component.html',
    styleUrls: ['./contact-profile.component.scss']
})
export class ContactProfileComponent implements OnInit {
    @Input() config;
    @Input() triggerSource;

    // #region Public properties
    clickIndex = -1;
    contact: Contact;
    connected = StringLiteralsPipe.translate('crm.connected');
    connectedBy = '';
    connectExchange = StringLiteralsPipe.translate('crm.connect_exchange');
    disconnect = StringLiteralsPipe.translate('crm.disconnect');
    displaySynchStatusData = [];
    exportToExchange = StringLiteralsPipe.translate('crm.export_to_exchange');
    exportToExchangeAndTakeOwnerShip = StringLiteralsPipe.translate('crm.export_to_exchangeAndTakeOwnerShip');
    isShowSynch = false;
    isAdminUser = false;
    noDataStyle = businessConstants.common.noDataStyle.none;
    resume = StringLiteralsPipe.translate('crm.resume');
    removeToOwnerShip = StringLiteralsPipe.translate('crm.remove_to_ownerShip');
    suspend = StringLiteralsPipe.translate('crm.suspend');
    synchNow = StringLiteralsPipe.translate('crm.synch_now');
    statusIcon: string;
    synchTitle = ContactInformationItemSubject.ContactSynch;
    takeOwnerShip = StringLiteralsPipe.translate('crm.take_ownerShip');
    userId = AppConfig.userId;
    // #endregion

    // #region Private properties
    private _currentUser;
    private _destroySubscriptions: Array<Subscription> = [];
    private _isNeedRefreshed = false;
    // #endregion

    // #region Constructor
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _contactService: ContactService,
        private _slideSheetService: SlideSheetService,
        private _storeQuerier: StoreQuerierService,
        private _toast: ToastService,
    ) {
        this._currentUser = this._storeQuerier.getCurrentUser();
    }
    // #endregion

    // #region Public functions
    ngOnInit() {
        this._setProfileStyleByData();
        if (this._currentUser) {
            this.isAdminUser = this._currentUser.credential.admin;
        }
        this._setSynchStatusByData();
    }

    /**
     * Remove ownership if link by other user.
     */
    clickRemoveOwnerShip(item, index) {
        this._isNeedRefreshed = true;
        this._updateContactSynchStatus({ 'operation': 'removeOwnerShip' });
    }

    /**
     * set params for click action.
     *
     * @param {*} item
     * @returns
     * @memberof ContactProfileComponent
     */
    clickSynchStatus(item, index) {
        if (item.indexOf(StringLiteralsPipe.translate('general.connected_by')) > -1) {
            return;
        }
        this.clickIndex = index;
        let param = {};
        switch (item) {
            case this.synchNow:
                this._isNeedRefreshed = true;
                param = { 'operation': 'sync' };
                break;
            case this.disconnect:
                this._isNeedRefreshed = false;
                param = { 'operation': 'disconnect' };
                break;
            case this.suspend:
                this._isNeedRefreshed = false;
                param = { 'operation': 'suspend' };
                break;
            case this.resume:
                this._isNeedRefreshed = false;
                param = { 'operation': 'resume' };
                break;
            case this.exportToExchange:
                this._isNeedRefreshed = false;
                param = { 'operation': 'sync', 'syncType': 1 };
                break;
            case this.exportToExchangeAndTakeOwnerShip:
                this._isNeedRefreshed = false;
                param = { 'operation': 'sync', 'syncType': 2 };
                break;
            case this.takeOwnerShip:
                this._isNeedRefreshed = true;
                param = { 'operation': 'takeOwnerShip' };
                break;
            case this.removeToOwnerShip:
                this._isNeedRefreshed = true;
                param = { 'operation': 'removeOwnerShip' };
                break;
            default:
                break;
        }
        this._updateContactSynchStatus(param);
    }

    setStatusDisabled(value) {
        return value.indexOf(StringLiteralsPipe.translate('general.connected_by')) > -1;
    }

    trackByFn(index, item) {
        return item.trackId;
    }
    // #endregion

    // #region Private functions
    /**
     * According suspend and syncType to set sych icon.
     *
     * @private
     * @memberof ContactProfileComponent
     */
    private _setStatusIcon() {
        this.statusIcon = '';
        if (this.contact.suspend) {
            this.statusIcon = businessConstants.svgIconName.contactStatus.disconnected;
        } else {
            if (this.contact.syncType === 1) {
                this.statusIcon = businessConstants.svgIconName.contactStatus.oneWayLinked;
            } else if (this.contact.syncType === 2) {
                this.statusIcon = businessConstants.svgIconName.contactStatus.synch;
            }
        }
    }

    private _setProfileStyleByData() {
        if (this.config.length === 0) {
            this.noDataStyle = businessConstants.common.noDataStyle.flex;
        } else {
            this.noDataStyle = businessConstants.common.noDataStyle.none;
        }
        this._setSynchStatusByData();
    }

    /**
     *Show synch status when loading or change action.
     *
     * @private
     * @memberof ContactProfileComponent
     */
    private _setSynchStatus() {
        this.connectedBy = '';
        this.displaySynchStatusData = [];
        // exsit ownerId and not equals current user. use connectBy and set status
        if (this.contact.ownerID && this.contact.ownerID !== AppConfig.userId) {
            this.connectedBy = `Connected by ${this.contact.ownerName}`;
            this.statusIcon = '';
            // set connection status by syncType
            if (!this.contact.syncType) {
                this.displaySynchStatusData.push(this.exportToExchange);
            } else {
                // according susend to judge synch status.
                if (this.contact.suspend === false) {
                    this.displaySynchStatusData = [this.synchNow, this.disconnect, this.suspend];
                } else if (this.contact.suspend) {
                    this.displaySynchStatusData = [this.synchNow, this.disconnect, this.resume];
                }
            }
            this.displaySynchStatusData.push(this.connectedBy);
        } else {
            // exist owner id and equals current user or no-exist user-id.
            if (!this.contact.suspend && !this.contact.syncType) {
                this.displaySynchStatusData = [this.exportToExchange, this.exportToExchangeAndTakeOwnerShip];
            } else if (this.contact.suspend === false) {
                this.displaySynchStatusData = [this.synchNow, this.disconnect, this.suspend];
            } else if (this.contact.suspend) {
                this.displaySynchStatusData = [this.synchNow, this.disconnect, this.resume];
            }
            this._setTakeOwnership();
        }

        this._setStatusIcon();
        this._updateChangesToUI();
    }

    private _setSynchStatusByData() {
        const systemConfig = this._storeQuerier.queryBySelector(getSystemConfigState);
        if (systemConfig && systemConfig.exchange) {
            this.isShowSynch = systemConfig.exchange['enable'];
        }
        const synchData = this.config.filter(item => item.subject && item.subject === ContactInformationItemSubject.ContactSynch);
        if (this.isShowSynch && synchData && synchData.length > 0) {
            this.contact = synchData[0].value;
            this._setSynchStatus();
        }
    }

    /**
     * According syncType to set one way or two way as a status.
     *
     * @private
     * @memberof ContactProfileComponent
     */
    private _setTakeOwnership() {
        if (this.contact.syncType === 2) {
            this.displaySynchStatusData.push(this.removeToOwnerShip);
        } else if (this.contact.syncType === 1) {
            this.displaySynchStatusData.push(this.takeOwnerShip);
        }
    }

    private _updateChangesToUI() {
        if (!this._changeDetectorRef[businessConstants.common.changeDetectorDestroyedIdentifier]) {
            this._changeDetectorRef.detectChanges();
        }
    }

    /**
     * Update Synch status when send request.
     * refresh slidesheet.
     *
     * @private
     * @param {*} params
     * @memberof ContactProfileComponent
     */
    private _updateContactSynchStatus(params) {
        this._contactService.synchTamaleIntoExchange(this.contact.id, params).pipe(
            take(1),
            catchError((error) => {
                this.clickIndex = -1;
                this._updateChangesToUI();
                this._toast.notify({
                    style: NotificationStyles.Error,
                    message: error
                });
                return observableOf(null);
            }),
            filter(res => !!res),
        ).subscribe(data => {
            this.clickIndex = -1;
            this.contact = Contact.parse(data);
            this._slideSheetService.slideSheetFeedbackSubject$.next({
                type: SlideSheetEventTypes.CONTACT_VALUE_CHANGE,
                payload: this.contact
            });
                this._setSynchStatus();
                if (this._isNeedRefreshed) {
                // Reload slide sheet when it's triggered on itself.
                if (this.triggerSource === TriggerSource.SlideSheet) {
                    this._slideSheetService.slideSheetActionSubject$.next({
                        type: SlideSheetActionTypes.CLOSE,
                        payload: ''
                    });
                    // Fix issue TAM-38451 [Chrome compatibility] [Exchange] - After upgrade to chrome 104, sync contact on sidesheet, sidesheet won't refresh automatically .
                    // Due to slidesheet action has dbouceTime(0),so set timeout equals 300ms for open contact panel action.
                    setTimeout(() => {
                        this._slideSheetService.slideSheetActionSubject$.next({
                            type: SlideSheetActionTypes.CONTACT_PANEL,
                            payload: this.contact.id
                        });
                    }, 300);
                }
            }

            this._updateChangesToUI();
        });
    }
    // #endregion
}


